import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiFailure, ERROR_COPY } from "../api";
import { Centered, Header, Shell } from "../components/Shell";
import { haptic } from "../telegram";

interface RoomPlayer {
  name: string;
  score: number;
  me: boolean;
  ready?: boolean;
  online?: boolean;
  answered?: boolean;
}

interface Room {
  phase: "lobby" | "q" | "reveal" | "done";
  round: number;
  qi: number;
  total: number;
  meReady?: boolean;
  players: RoomPlayer[];
  question?: { t: string; img?: string; options: string[] };
  msLeft?: number;
  correct?: number;
  myAnswer?: number | null;
  myPoints?: number;
}

const POLL_MS = 1200;

/**
 * «Арена» — live quiz against the chat, one shared room per day. Everything
 * happens inside a Durable Object; this screen just polls it and renders.
 * No streaks, no result sheet — the finale IS the result.
 */
export function Arena({ date: _date }: { date?: string | undefined }) {
  const [room, setRoom] = useState<Room | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  const op = useCallback(async (name: string, extra: Record<string, unknown> = {}) => {
    try {
      const r = await api.arena<Room>(name, extra);
      setRoom(r);
      setError(null);
      return r;
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setError(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "");
      // Switched off: stop polling, or the next state poll would clear the error.
      if (code === "game_disabled" && timer.current) {
        window.clearInterval(timer.current);
        timer.current = null;
      }
      return null;
    }
  }, []);

  // Join once, then poll.
  useEffect(() => {
    void op("join");
    timer.current = window.setInterval(() => void op("state"), POLL_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [op]);

  const answer = async (j: number) => {
    if (!room || busy || !room.meReady || room.myAnswer != null) return;
    setBusy(true);
    haptic.tap();
    await op("answer", { i: room.qi, j });
    setBusy(false);
  };

  if (error) {
    return (
      <Shell>
        <Centered>
          <div>{error}</div>
        </Centered>
      </Shell>
    );
  }
  if (!room) return <Shell><Centered>…</Centered></Shell>;

  const leader = room.players[0];

  return (
    <Shell header={<Header left={room.phase === "lobby" ? "лобі" : `${room.qi + 1}/${room.total}`} title="АРЕНА" right="🏟️" />}>
      <div className="flex flex-1 flex-col px-4 pb-4">
        {room.phase === "lobby" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <div className="text-center text-sm text-tghint">
              живий квіз: {room.total} питань, 15 секунд на кожне.
              <br />
              зови всіх у чаті - грає той, хто натисне «я готовий»
            </div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {room.players.map((p) => (
                <span
                  key={p.name}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                    p.me ? "bg-tgbutton text-tgbuttontext" : "bg-tgsection"
                  } ${!p.online ? "opacity-40" : ""}`}
                >
                  {p.name} {!p.online ? "😴" : p.ready ? "✅" : "🤔"}
                </span>
              ))}
            </div>
            {!room.meReady ? (
              <button
                type="button"
                onClick={() => void op("ready")}
                className="rounded-xl bg-tgbutton px-8 py-3 text-[15px] font-bold text-tgbuttontext active:brightness-90"
              >
                ✅ Я готовий
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void op("start")}
                  className="rounded-xl bg-tgbutton px-8 py-3 text-[15px] font-bold text-tgbuttontext active:brightness-90"
                >
                  🚀 Почати ({room.players.filter((p) => p.ready).length})
                </button>
                <button type="button" onClick={() => void op("unready")} className="text-xs text-tghint active:opacity-70">
                  передумав
                </button>
              </>
            )}
            <div className="text-[11px] text-tghint">хто без «готовий» - дивиться; долучитись можна й на льоту</div>
          </div>
        )}

        {(room.phase === "q" || room.phase === "reveal") && room.question && (
          <div className="flex flex-1 flex-col justify-center gap-3">
            {room.phase === "q" && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-[rgba(128,128,128,0.2)]">
                <div
                  className="h-full rounded-full bg-tgbutton"
                  style={{ width: `${Math.round(((room.msLeft ?? 0) / 15000) * 100)}%`, transition: "width 1.2s linear" }}
                />
              </div>
            )}
            <div className="rounded-2xl bg-tgsection px-5 py-5 text-center">
              <div className="text-lg font-bold leading-snug">{room.question.t}</div>
              {room.question.img && (
                <img src={room.question.img} alt="" className="mx-auto mt-3 h-20 rounded-md border border-[rgba(128,128,128,0.25)]" />
              )}
            </div>
            <div className="grid grid-cols-1 gap-2">
              {room.question.options.map((o, j) => {
                const mine = room.myAnswer === j;
                const tone =
                  room.phase === "reveal"
                    ? j === room.correct
                      ? "bg-ok/30"
                      : mine
                        ? "bg-cold/30"
                        : "bg-tgsection opacity-60"
                    : mine
                      ? "bg-tgbutton/30"
                      : room.myAnswer != null
                        ? "bg-tgsection opacity-60"
                        : "bg-tgsection active:opacity-70";
                return (
                  <button
                    key={j}
                    type="button"
                    disabled={busy || room.phase === "reveal" || !room.meReady || room.myAnswer != null}
                    onClick={() => void answer(j)}
                    className={`rounded-xl px-4 py-3 text-left text-[15px] font-semibold ${tone}`}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
            {!room.meReady && (
              <button
                type="button"
                onClick={() => void op("ready")}
                className="mx-auto rounded-xl bg-tgbutton px-6 py-2.5 text-sm font-bold text-tgbuttontext active:brightness-90"
              >
                🙋 Долучитись з цього питання
              </button>
            )}
            {room.phase === "reveal" && room.meReady && (
              <div className="text-center text-sm font-semibold">
                {room.myAnswer === room.correct ? `✅ +${room.myPoints}` : room.myAnswer == null ? "⌛ не встиг" : "❌ повз"}
              </div>
            )}
            <div className="flex flex-wrap justify-center gap-1.5 text-xs">
              {room.players.map((p) => (
                <span
                  key={p.name}
                  className={`rounded-full px-2 py-0.5 ${p.me ? "bg-tgbutton/25" : "bg-tgsection"} ${!p.online ? "opacity-40" : ""}`}
                >
                  {p.name} {room.phase === "q" ? (p.answered ? "✓" : p.online ? "…" : "😴") : ""} <b>{p.score}</b>
                </span>
              ))}
            </div>
          </div>
        )}

        {room.phase === "done" && (
          <div className="flex flex-1 flex-col justify-center gap-3">
            <div className="text-center text-2xl font-extrabold">🏆 {leader?.name ?? "?"}</div>
            <ul className="mx-auto w-full max-w-[380px] space-y-1.5">
              {room.players.map((p, i) => (
                <li key={p.name} className={`flex items-center justify-between rounded-xl px-4 py-2.5 ${p.me ? "bg-tgbutton/20" : "bg-tgsection"}`}>
                  <span className="font-semibold">
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`} {p.name}
                  </span>
                  <span className="font-bold tabular-nums">{p.score}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => void op("again")}
              className="mx-auto rounded-xl bg-[rgba(128,128,128,0.2)] px-6 py-2.5 text-sm font-semibold active:opacity-70"
            >
              🔁 Ще раунд
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}
